import { useTheme } from "@emotion/react";
import {
  Avatar,
  Box,
  Button,
  Divider,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/ErrorOutline";
import { getTime, getTimeFromNow } from "utils/timeHelper";
import { LoadingButton } from "@mui/lab";
import { readNotificationThunk } from "store/reducers/notifications/notificationsSlice";
import { useNavigate } from "react-router-dom";
import {
  combinedNotificationSelector,
  getCombinedNotificationsThunk,
  readCombinedNotificationThunk,
} from "store/reducers/notifications/combinedNotificationsSlice";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import ArrowinwardIcon from "@mui/icons-material/CallReceived";
import ArrowReturnIcon from "@mui/icons-material/TurnLeft";

const NotificationList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const notifications = useSelector(combinedNotificationSelector.selectAll);
  const combined_notification_state = useSelector(
    (state) => state.combinedNotifications.status
  );
  const notification_state = useSelector((state) => state.notifications.status);
  const page_size = useSelector(
    (state) => state.combinedNotifications.page_size
  );

  const [page, setPage] = useState(1);
  useEffect(() => {
    dispatch(getCombinedNotificationsThunk(page));
  }, [page]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const avatarSX = {
    width: 36,
    height: 36,
    fontSize: "1rem",
  };
  const actionSX = {
    mt: "6px",
    ml: 1,
    top: "auto",
    right: "auto",
    alignSelf: "flex-start",

    transform: "none",
  };

  const theme = useTheme();
  return (
    <Box padding={2}>
      <List
        component="nav"
        sx={{
          p: 0,
          "& .MuiListItemButton-root": {
            py: 0.5,
            "& .MuiAvatar-root": avatarSX,
            "& .MuiListItemSecondaryAction-root": {
              ...actionSX,
              position: "relative",
            },
          },
        }}
      >
        {notifications.map((notification, index) => (
          <Box
            key={notification.id}
            sx={{
              ...(notification.read && {
                bgcolor: theme.palette.action.selected,
              }),
            }}
          >
            <ListItemButton>
              <ListItemAvatar>
                {notification.type === "error" && (
                  <Avatar
                    sx={{
                      color: "error.main",
                      bgcolor: "error.lighter",
                    }}
                  >
                    <ErrorIcon />
                  </Avatar>
                )}

                {(notification.type === "stock" ||
                  notification.type === "inv_low") && (
                  <Avatar
                    sx={{
                      color: "warning.main",
                      bgcolor: "warning.lighter",
                    }}
                  >
                    <WarningAmberIcon />
                  </Avatar>
                )}
                {notification.type === "inv_transfer" && (
                  <Avatar
                    sx={{
                      color: "success.main",
                      bgcolor: "success.lighter",
                    }}
                  >
                    <ArrowOutwardIcon />
                  </Avatar>
                )}
                {notification.type === "inv_received" && (
                  <Avatar
                    sx={{
                      color: "success.main",
                      bgcolor: "success.lighter",
                    }}
                  >
                    <ArrowinwardIcon />
                  </Avatar>
                )}
                {notification.type === "inv_return" && (
                  <Avatar
                    sx={{
                      color: "success.main",
                      bgcolor: "success.lighter",
                    }}
                  >
                    <ArrowReturnIcon />
                  </Avatar>
                )}
              </ListItemAvatar>
              {notification.type === "error" && (
                <ListItemText
                  primary={
                    <Typography variant="h6">
                      Error in{" "}
                      <Typography component="span" variant="subtitle1">
                        {notification.machine_detail.name}
                      </Typography>
                      <br />
                      {notification.message.error}
                    </Typography>
                  }
                  secondary={getTimeFromNow(notification.created_date)}
                />
              )}

              {notification.type === "stock" && (
                <ListItemText
                  primary={
                    <Typography variant="h6">
                      Low stock in{" "}
                      <Typography component="span" variant="subtitle1">
                        {notification.machine_detail.name}
                      </Typography>
                      <br />
                      Product "{notification.message.product_name}" stock is low
                      in tray {notification.message.tray_no} partition{" "}
                      {notification.message.partition_no}
                    </Typography>
                  }
                  secondary={<>{getTimeFromNow(notification.created_date)}</>}
                />
              )}

              {notification.type === "inv_low" && (
                <ListItemText
                  primary={
                    <Typography variant="h6">
                      Low stock in{" "}
                      <Typography component="span" variant="subtitle1">
                        Your inventory
                      </Typography>
                      <br />
                      Product "{notification.message.product_name}" items left{" "}
                      {notification.message.qty}
                    </Typography>
                  }
                  secondary={getTimeFromNow(notification.created_date)}
                />
              )}

              {notification.type === "inv_transfer" && (
                <ListItemText
                  primary={
                    <Typography variant="h6">
                      Products delivered to{" "}
                      <Typography component="span" variant="subtitle1">
                        {notification.message.source}
                      </Typography>
                      <br />
                      Product "{notification.message.product_name}" Qty{" "}
                      {notification.message.qty}
                    </Typography>
                  }
                  secondary={getTimeFromNow(notification.created_date)}
                />
              )}

              {notification.type === "inv_return" && (
                <ListItemText
                  primary={
                    <Typography variant="h6">
                      Products returned to{" "}
                      <Typography component="span" variant="subtitle1">
                        {notification.message.source}
                      </Typography>
                      <br />
                      Product "{notification.message.product_name}" Qty{" "}
                      {notification.message.qty}
                    </Typography>
                  }
                  secondary={getTimeFromNow(notification.created_date)}
                />
              )}

              {notification.type === "inv_received" && (
                <ListItemText
                  primary={
                    <Typography variant="h6">
                      Products received from{" "}
                      <Typography component="span" variant="subtitle1">
                        {notification.message.source}
                      </Typography>
                      <br />
                      Product "{notification.message.product_name}" Qty{" "}
                      {notification.message.qty}
                    </Typography>
                  }
                  secondary={getTimeFromNow(notification.created_date)}
                />
              )}

              <ListItemSecondaryAction>
                <Typography variant="caption" noWrap>
                  {getTime(notification.created_date)}
                </Typography>
              </ListItemSecondaryAction>
            </ListItemButton>
            <Stack direction="row" justifyContent="center">
              {!notification.read && (
                <LoadingButton
                  color="info"
                  variant="outlined"
                  size="small"
                  loading={
                    (notification_state?.loading === "read" &&
                      notification_state?.id === notification.id) ||
                    (combined_notification_state?.loading === "read" &&
                      combined_notification_state?.id === notification.id)
                  }
                  sx={{ ml: 1, my: 1, width: "14ch" }}
                  onClick={() => {
                    dispatch(readCombinedNotificationThunk(notification));
                  }}
                >
                  Mark as read
                </LoadingButton>
              )}

              {notification.type === "error" && (
                <Button
                  color="info"
                  variant="outlined"
                  size="small"
                  sx={{ ml: 1, my: 1, width: "14ch" }}
                  onClick={() => {
                    navigate(`/machines/${notification.machine_detail.id}`);
                  }}
                >
                  View machine
                </Button>
              )}

              {notification.type === "stock" && (
                <Button
                  color="info"
                  variant="outlined"
                  size="small"
                  sx={{ ml: 1, my: 1, width: "14ch" }}
                  onClick={() => {
                    navigate(
                      `/machines/${notification.machine_detail.id}/refill`
                    );
                  }}
                >
                  Refill
                </Button>
              )}
            </Stack>
            <Divider />
          </Box>
        ))}
      </List>
      <Stack direction="row" justifyContent="center" marginTop={2}>
        <Pagination
          count={page_size}
          variant="outlined"
          shape="rounded"
          onChange={handlePageChange}
        />
      </Stack>
    </Box>
  );
};

export default NotificationList;
